import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  CheckCircle,
  Clock,
  Download,
  Eye,
  Info,
  Wallet,
  X,
  XCircle,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import NewtonsCradleLoader from '../components/common/NewtonsCradleLoader';
import apiService, {
  extractAffiliateFundsList,
  extractAffiliateFundsPagination,
  extractAffiliateFundsSummary,
} from '../utils/apiService';
import { getThemeColor } from '../config/appConfig';
import { useFetchOnceByKey } from '../hooks/useMountOnce';

const THEME = getThemeColor('primary');
const ALL_PAGE_SIZE = 10000;

const resolveApiPageSize = (pageSize) => (pageSize === 'all' ? ALL_PAGE_SIZE : Number(pageSize) || 10);
const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: 'all', label: 'All' },
];

const toAmount = (val) => Number(val) || 0;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(toAmount(amount));

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const normalizeStatus = (status) => {
  const s = (status || '').toString().toLowerCase().trim();
  if (/(complete|approved|success|paid)/.test(s)) return 'completed';
  if (/(pending|process|await|review|submitted)/.test(s)) return 'pending';
  if (/(reject|fail|cancel|declin|denied)/.test(s)) return 'failed';
  return 'other';
};

const formatTransactionNumber = (fund) => {
  const value = fund?.TransactionNumber ?? fund?.transactionNumber ?? '';
  const trimmed = value?.toString().trim();
  return trimmed || '—';
};

const transformFund = (fund) => ({
  id: fund?.affiliateFundId ?? fund?.id,
  txnId: formatTransactionNumber(fund),
  amount: toAmount(fund?.amount),
  type: fund?.accountType || fund?.type || '—',
  method: fund?.accountBank || fund?.method || '—',
  status: fund?.status,
  date: fund?.createdDateTime,
  remarks: fund?.remarks || fund?.Remarks || fund?.adminRemarks || fund?.rejectRemarks || '',
});

const StatusBadge = ({ status }) => {
  const key = normalizeStatus(status);
  if (key === 'completed') {
    return <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">Completed</span>;
  }
  if (key === 'pending') {
    return <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">Pending</span>;
  }
  if (key === 'failed') {
    return <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-700">{status || 'Reject'}</span>;
  }
  return <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 capitalize">{status || '—'}</span>;
};

const PayoutDonutChart = ({ completed, pending, failed, total, isDarkMode }) => {
  const circumference = 2 * Math.PI * 42;
  const safeCompleted = toAmount(completed);
  const safePending = toAmount(pending);
  const safeFailed = toAmount(failed);
  const sum = safeCompleted + safePending + safeFailed || 1;
  const segments = [
    { value: safeCompleted, color: THEME },
    { value: safePending, color: '#F97316' },
    { value: safeFailed, color: '#EF4444' },
  ];
  let offset = 0;

  return (
    <div className="relative w-36 h-36 mx-auto">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="42" fill="none" stroke={isDarkMode ? '#374151' : '#F3F4F6'} strokeWidth="10" />
        {segments.map((seg, i) => {
          const len = (seg.value / sum) * circumference;
          const dash = `${len} ${circumference - len}`;
          const el = (
            <circle
              key={i}
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke={seg.color}
              strokeWidth="10"
              strokeDasharray={dash}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
        <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(total)}</span>
        <span className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Payouts</span>
      </div>
    </div>
  );
};

const SummaryCard = ({ title, value, subtitle, icon: Icon, iconBg, iconColor, isDarkMode }) => (
  <div className={`rounded-2xl border p-4 shadow-sm ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{title}</p>
        <p className={`text-xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</p>
        <p className={`text-[11px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{subtitle}</p>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
    </div>
  </div>
);

const mapFilterStatusToApi = (status) => {
  if (status === 'completed') return 'Completed';
  if (status === 'pending') return 'Pending';
  if (status === 'failed') return 'Reject';
  return undefined;
};

const buildFundsQueryParams = (page, size, activeFilters) => ({
  pageNumber: page,
  pageSize: size,
  searchTerm: '',
  fromDate: activeFilters.dateFrom || undefined,
  toDate: activeFilters.dateTo || undefined,
  status: mapFilterStatusToApi(activeFilters.status),
});

const fetchAllPayoutsFromApi = async (activeFilters) => {
  const size = ALL_PAGE_SIZE;
  const allRows = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await apiService.getAffiliateFunds(
      buildFundsQueryParams(page, size, activeFilters),
      { force: true }
    );
    const list = extractAffiliateFundsList(response).map(transformFund);
    const meta = extractAffiliateFundsPagination(response, size);
    allRows.push(...list);
    totalPages = meta.totalPages || 1;
    if (list.length === 0) break;
    page += 1;
  } while (page <= totalPages);

  return allRows;
};

const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const EMPTY_FUNDS_SUMMARY = {
  hasData: false,
  hasCounts: false,
  total: 0,
  completedAmount: 0,
  completedCount: 0,
  pendingAmount: 0,
  pendingCount: 0,
  failedAmount: 0,
  failedCount: 0,
};

const formatSummaryCurrency = (value, hasData, loading) => {
  if (loading) return '…';
  if (!hasData) return '—';
  return formatCurrency(value);
};

const formatSummaryCount = (value, hasData, hasCounts, loading) => {
  if (loading) return '…';
  if (!hasData) return '—';
  if (!hasCounts) return 'Amount from API';
  return `${value} Transaction${value === 1 ? '' : 's'}`;
};

const PayoutHistory = () => {
  const { isDarkMode } = useTheme();
  const [payouts, setPayouts] = useState([]);
  const [fundsSummary, setFundsSummary] = useState(EMPTY_FUNDS_SUMMARY);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('10');
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 1,
    pageNumber: 1,
    pageSize: 10,
  });
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: 'all',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: 'all',
  });
  const [isExporting, setIsExporting] = useState(false);

  const apiPageSize = resolveApiPageSize(pageSize);
  const listFetchKey = `payouts|${currentPage}|${apiPageSize}|${appliedFilters.dateFrom}|${appliedFilters.dateTo}|${appliedFilters.status}`;

  const fetchPayouts = useCallback(async ({ cancelled }) => {
    setIsLoading(true);
    try {
      const response = await apiService.getAffiliateFunds(
        buildFundsQueryParams(currentPage, apiPageSize, appliedFilters)
      );
      if (cancelled()) return;

      const list = extractAffiliateFundsList(response).map(transformFund);
      const meta = extractAffiliateFundsPagination(response, apiPageSize);
      setPayouts(list);
      setPagination(meta);
      setFundsSummary(extractAffiliateFundsSummary(response));
    } catch {
      if (!cancelled()) {
        setPayouts([]);
        setPagination({ totalRecords: 0, totalPages: 1, pageNumber: currentPage, pageSize: apiPageSize });
        setFundsSummary(EMPTY_FUNDS_SUMMARY);
      }
    } finally {
      if (!cancelled()) setIsLoading(false);
    }
  }, [currentPage, apiPageSize, appliedFilters]);

  useFetchOnceByKey(listFetchKey, fetchPayouts, true);

  const summary = fundsSummary;
  const summaryTransactionCount = summary.hasCounts
    ? summary.completedCount + summary.pendingCount + summary.failedCount
    : pagination.totalRecords;

  const totalPages = pageSize === 'all' ? 1 : Math.max(1, pagination.totalPages);
  const effectivePageSize = apiPageSize;
  const totalRecords = pagination.totalRecords || payouts.length;
  const showingFrom = totalRecords === 0 ? 0 : (currentPage - 1) * effectivePageSize + 1;
  const showingTo = pageSize === 'all'
    ? totalRecords
    : Math.min(currentPage * effectivePageSize, totalRecords);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    const empty = { dateFrom: '', dateTo: '', status: 'all' };
    setFilters(empty);
    setAppliedFilters(empty);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    setCurrentPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const exportRows = await fetchAllPayoutsFromApi(appliedFilters);
      if (!exportRows.length) return;

      const headers = ['S.No', 'Transaction #', 'Requested Date', 'Amount', 'Status'];
      const rows = exportRows.map((p, idx) => [
        idx + 1,
        p.txnId,
        formatDate(p.date),
        formatCurrency(p.amount),
        p.status || '—',
      ]);
      const csv = [headers, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payout-statement-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    } finally {
      setIsExporting(false);
    }
  };

  const card = isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100';
  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition focus:border-red-800 focus:ring-2 focus:ring-red-800/15 ${
    isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
  }`;


  return (
    <div className={`min-h-full ${isDarkMode ? 'bg-black' : 'bg-[#F4F5F7]'}`}>
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-5 sm:py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-2xl font-bold ${text}`}>Payout History</h1>
          <p className={`text-sm mt-1 ${muted}`}>
            <Link to="/" className="hover:text-red-800">Dashboard</Link>
            <span className="mx-1.5">›</span>
            <span>Payout History</span>
          </p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            title="Total Payouts"
            value={formatSummaryCurrency(summary.total, summary.hasData, isLoading)}
            subtitle={
              isLoading
                ? 'Loading…'
                : summary.hasData
                  ? `${pagination.totalRecords || summaryTransactionCount} records`
                  : 'No summary data'
            }
            icon={Wallet}
            iconBg="bg-red-50"
            iconColor="text-red-800"
            isDarkMode={isDarkMode}
          />
          <SummaryCard
            title="Completed Payouts"
            value={formatSummaryCurrency(summary.completedAmount, summary.hasData, isLoading)}
            subtitle={summary.hasCounts || isLoading
              ? formatSummaryCount(summary.completedCount, summary.hasData, summary.hasCounts, isLoading)
              : ''}
            icon={CheckCircle}
            iconBg="bg-emerald-50"
            iconColor="text-emerald-600"
            isDarkMode={isDarkMode}
          />
          <SummaryCard
            title="Pending Payouts"
            value={formatSummaryCurrency(summary.pendingAmount, summary.hasData, isLoading)}
            subtitle={formatSummaryCount(summary.pendingCount, summary.hasData, summary.hasCounts, isLoading)}
            icon={Clock}
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
            isDarkMode={isDarkMode}
          />
          <SummaryCard
            title="Reject Payouts"
            value={formatSummaryCurrency(summary.failedAmount, summary.hasData, isLoading)}
            subtitle={formatSummaryCount(summary.failedCount, summary.hasData, summary.hasCounts, isLoading)}
            icon={XCircle}
            iconBg="bg-red-50"
            iconColor="text-red-600"
            isDarkMode={isDarkMode}
          />
        </div>

        {/* Top filters */}
        <div className={`rounded-2xl border p-4 sm:p-5 shadow-sm mb-6 ${card}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${muted}`}>From Date</label>
              <div className="relative">
                <Calendar className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${muted}`} />
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                  className={`${inputCls} pl-8`}
                />
              </div>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${muted}`}>To Date</label>
              <div className="relative">
                <Calendar className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${muted}`} />
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                  className={`${inputCls} pl-8`}
                />
              </div>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${muted}`}>Status</label>
              <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className={inputCls}>
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Reject</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleApplyFilters}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white"
                style={{ backgroundColor: THEME }}
              >
                Apply
              </button>
              <button
                type="button"
                onClick={handleResetFilters}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium border ${isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-6">
          {/* Main table */}
          <div className="space-y-4">
            <div className={`rounded-2xl border shadow-sm overflow-hidden ${card}`}>
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                <h2 className={`text-sm font-semibold ${text}`}>Payout History</h2>
              </div>

              {isLoading ? (
                <div className="flex justify-center py-16">
                  <NewtonsCradleLoader />
                </div>
              ) : payouts.length === 0 ? (
                <div className={`text-center py-16 px-4 ${muted}`}>
                  <p className="text-sm">No payout records found.</p>
                  <Link to="/withdrawal" className="inline-block mt-3 text-sm font-medium text-red-800 hover:underline">
                    Request a withdrawal
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[620px]">
                    <thead>
                      <tr className={`text-left text-xs font-medium border-b ${isDarkMode ? 'border-gray-800 text-gray-400' : 'border-gray-100 text-gray-500'}`}>
                        <th className="px-5 py-3 w-14">S.No</th>
                        <th className="px-3 py-3">Transaction #</th>
                        <th className="px-3 py-3">Requested Date</th>
                        <th className="px-3 py-3">Amount</th>
                        <th className="px-3 py-3">Status</th>
                        <th className="px-5 py-3 text-center">View</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-gray-800' : 'divide-gray-50'}`}>
                        {payouts.map((payout, idx) => (
                        <tr key={payout.id ?? `${payout.txnId}-${idx}`} className={isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50/80'}>
                          <td className={`px-5 py-3.5 text-sm ${muted}`}>{(currentPage - 1) * effectivePageSize + idx + 1}</td>
                          <td className={`px-3 py-3.5 text-sm font-mono ${text}`}>{payout.txnId}</td>
                          <td className={`px-3 py-3.5 text-sm ${text}`}>{formatDate(payout.date)}</td>
                          <td className={`px-3 py-3.5 text-sm font-semibold ${text}`}>{formatCurrency(payout.amount)}</td>
                          <td className="px-3 py-3.5">
                            <StatusBadge status={payout.status} />
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <button
                              type="button"
                              onClick={() => setSelectedPayout({
                                ...payout,
                                serialNo: (currentPage - 1) * effectivePageSize + idx + 1,
                              })}
                              className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition ${
                                isDarkMode ? 'border-gray-700 hover:bg-gray-800 text-gray-400' : 'border-gray-200 hover:bg-gray-50 text-gray-500'
                              }`}
                              aria-label="View details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!isLoading && totalRecords > 0 && (
                <div className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-t text-xs ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className={muted}>Show</span>
                      <select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(e.target.value)}
                        className={`rounded-lg border px-2 py-1.5 text-xs outline-none focus:border-red-800 focus:ring-2 focus:ring-red-800/15 ${
                          isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
                        }`}
                      >
                        {PAGE_SIZE_OPTIONS.map((opt) => (
                          <option key={opt.label} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <span className={muted}>records</span>
                    </div>
                    <p className={muted}>
                      Showing {showingFrom} to {showingTo} of {totalRecords} entries
                    </p>
                  </div>
                  {pageSize !== 'all' && totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={currentPage === 1 || isLoading}
                      onClick={() => setCurrentPage((p) => p - 1)}
                      className={`px-3 py-1.5 rounded-lg border disabled:opacity-40 ${
                        isDarkMode ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-600'
                      }`}
                    >
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                      .map((page, i, arr) => (
                        <React.Fragment key={page}>
                          {i > 0 && arr[i - 1] !== page - 1 && <span className={`px-1 ${muted}`}>…</span>}
                          <button
                            type="button"
                            disabled={isLoading}
                            onClick={() => setCurrentPage(page)}
                            className={`w-8 h-8 rounded-lg text-xs font-medium ${
                              currentPage === page
                                ? 'text-white'
                                : isDarkMode
                                  ? 'text-gray-400 hover:bg-gray-800'
                                  : 'text-gray-600 hover:bg-gray-100'
                            }`}
                            style={currentPage === page ? { backgroundColor: THEME } : {}}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}
                    <button
                      type="button"
                      disabled={currentPage === totalPages || isLoading}
                      onClick={() => setCurrentPage((p) => p + 1)}
                      className={`px-3 py-1.5 rounded-lg border disabled:opacity-40 ${
                        isDarkMode ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-600'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                  )}
                </div>
              )}
            </div>

            {/* Info note */}
            <div className={`flex items-start gap-3 rounded-2xl border px-4 py-3.5 ${isDarkMode ? 'bg-red-950/40 border-red-900' : 'bg-red-50 border-red-100'}`}>
              <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: THEME }} />
              <p className={`text-xs ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
                Payouts are processed within 24 business hours. For any queries, please contact support.
              </p>
            </div>
          </div>

          {/* Right sidebar */}
          <div className="hidden xl:block space-y-4">
            <div className={`rounded-2xl border p-5 shadow-sm ${card}`}>
              <h3 className={`text-sm font-semibold mb-1 ${text}`}>Payout Summary</h3>
              <p className={`text-xs mb-4 ${muted}`}>
                {isLoading
                  ? 'Loading summary…'
                  : summary.hasData
                    ? `${summaryTransactionCount} transaction${summaryTransactionCount === 1 ? '' : 's'}`
                    : 'No summary data from API'}
              </p>
              {isLoading ? (
                <div className="flex justify-center py-10">
                  <NewtonsCradleLoader />
                </div>
              ) : summary.hasData ? (
                <>
                  <PayoutDonutChart
                    completed={summary.completedAmount}
                    pending={summary.pendingAmount}
                    failed={summary.failedAmount}
                    total={summary.total}
                    isDarkMode={isDarkMode}
                  />
                  <div className="mt-4 space-y-2 text-xs">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: THEME }} />
                        <span className={muted}>
                          Completed{summary.hasCounts ? ` (${summary.completedCount})` : ''}
                        </span>
                      </span>
                      <span className={`font-medium shrink-0 ${text}`}>
                        {formatCurrency(summary.completedAmount)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full bg-orange-500 shrink-0" />
                        <span className={muted}>
                          Pending{summary.hasCounts ? ` (${summary.pendingCount})` : ''}
                        </span>
                      </span>
                      <span className={`font-medium shrink-0 ${text}`}>
                        {formatCurrency(summary.pendingAmount)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
                        <span className={muted}>
                          Reject{summary.hasCounts ? ` (${summary.failedCount})` : ''}
                        </span>
                      </span>
                      <span className={`font-medium shrink-0 ${text}`}>
                        {formatCurrency(summary.failedAmount)}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className={`py-10 text-center text-sm ${muted}`}>No summary data available</div>
              )}
            </div>

            <div className={`rounded-2xl border p-5 shadow-sm ${card}`}>
              <h3 className={`text-sm font-semibold mb-2 ${text}`}>Download Statement</h3>
              <p className={`text-xs mb-4 ${muted}`}>
                Download a detailed statement of all your payout transactions.
              </p>
              <button
                type="button"
                onClick={handleExport}
                disabled={isExporting}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition disabled:opacity-60 ${
                  isDarkMode ? 'border-red-800 text-red-300 hover:bg-red-950' : 'border-red-200 text-red-800 hover:bg-red-50'
                }`}
              >
                <Download className="w-4 h-4" />
                {isExporting ? 'Downloading…' : 'Download Statement'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {selectedPayout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedPayout(null)}>
          <div
            className={`w-full max-w-md rounded-2xl border shadow-xl p-6 ${card}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className={`text-base font-semibold ${text}`}>Transaction Details</h3>
              <button type="button" onClick={() => setSelectedPayout(null)} className={muted}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              {[
                ['S.No', selectedPayout.serialNo],
                ['Transaction #', selectedPayout.txnId],
                ['Requested Date', formatDate(selectedPayout.date)],
                ['Amount', formatCurrency(selectedPayout.amount)],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4">
                  <span className={muted}>{label}</span>
                  <span className={`font-medium text-right ${text}`}>{value}</span>
                </div>
              ))}
              <div className="flex justify-between items-center gap-4">
                <span className={muted}>Status</span>
                <StatusBadge status={selectedPayout.status} />
              </div>
              <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                <span className={`block text-xs font-medium mb-1.5 ${muted}`}>Remarks</span>
                <p className={`text-sm rounded-xl border px-3 py-2.5 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-800'}`}>
                  {selectedPayout.remarks?.trim() ? selectedPayout.remarks : '—'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutHistory;
